/**
 * Product Registration Types
 *
 * Barrel file that re-exports schemas split across:
 *   - product-basic-info.ts   (Overview step)
 *   - product-pricing.ts      (Nutritions, Vitamins, Minerals, Amino Acids)
 *   - product-inventory.ts    (Packaging, Other)
 *
 * Also defines the combined ProductRegistrationSchema, stepper config,
 * and default-value factories.
 *
 * Field specs derived from Form_Field_Specification_By_Tab.xlsx
 */
import { z } from 'zod';

// Re-export every schema/type from the split files so existing
// imports from './product-form' keep working without changes.
export {
  optionalDecimal,
  optionalString,
  BasicInformationSchema,
  DescriptionSchema,
  AllergensSchema,
  VerificationSchema,
  ExpirationSchema,
  ExpiryUnitSchema,
  OverviewSchema,
} from './product-basic-info';
export type { OverviewFormValues, ExpiryUnit } from './product-basic-info';

export {
  CaloricInformationSchema,
  ProteinSchema,
  FatSchema,
  CarbohydratesSchema,
  ServingInformationSchema,
  IngredientsSchema,
  DietaryInformationSchema,
  NutritionsSchema,
  BVitaminsSchema,
  FatSolubleVitaminsSchema,
  OtherVitaminsSchema,
  VitaminsSchema,
  ElectrolytesSchema,
  MajorMineralsSchema,
  TraceMineralsSchema,
  MineralsSchema,
  AminoAcidDerivativesSchema,
  EssentialAminoAcidsSchema,
  NonEssentialAminoAcidsSchema,
  AminoAcidsSchema,
} from './product-pricing';
export type {
  NutritionsFormValues,
  VitaminsFormValues,
  MineralsFormValues,
  AminoAcidsFormValues,
} from './product-pricing';

export {
  PrimaryPackageSchema,
  CartonCaseSchema,
  StorageSchema,
  PackagingSchema,
  IdentifiersSchema,
  OtherCompoundsSchema,
  ReviewSimilarProductsSchema,
  OtherSchema,
} from './product-inventory';
export type { PrimaryPackageFormValues, PackagingFormValues, OtherFormValues } from './product-inventory';

// Internal imports for the combined schema
import { OverviewSchema } from './product-basic-info';
import { NutritionsSchema, VitaminsSchema, MineralsSchema, AminoAcidsSchema } from './product-pricing';
import { PackagingSchema, OtherSchema } from './product-inventory';

// Also pull in inferred types needed by default-value factories
import type { OverviewFormValues } from './product-basic-info';
import type { NutritionsFormValues, VitaminsFormValues, MineralsFormValues, AminoAcidsFormValues } from './product-pricing';
import type { PackagingFormValues, OtherFormValues } from './product-inventory';

// -- Combined Product Registration Payload ----------------------
export const ProductRegistrationSchema = z.object({
  overview: OverviewSchema,
  nutritions: NutritionsSchema,
  vitamins: VitaminsSchema,
  minerals: MineralsSchema,
  amino_acids: AminoAcidsSchema,
  packaging: PackagingSchema,
  other: OtherSchema,
});

export type ProductRegistrationPayload = z.infer<typeof ProductRegistrationSchema>;

// -- Stepper Definitions ----------------------------------------
export type ProductStep = 'overview' | 'nutritions' | 'vitamins' | 'minerals' | 'amino_acids' | 'packaging' | 'other';

export interface ProductStepConfig {
  key: ProductStep;
  label: string;
  index: number;
  /** True for steps with no mandatory fields — see FormStepper's `optional` flag. */
  optional?: boolean;
}

export const PRODUCT_STEPS: ProductStepConfig[] = [
  { key: 'overview', label: 'Overview', index: 0 },
  { key: 'nutritions', label: 'Nutritions', index: 1, optional: true },
  { key: 'vitamins', label: 'Vitamins', index: 2, optional: true },
  { key: 'minerals', label: 'Minerals', index: 3, optional: true },
  { key: 'amino_acids', label: 'Amino Acids', index: 4, optional: true },
  { key: 'packaging', label: 'Packaging', index: 5 },
  { key: 'other', label: 'Other', index: 6, optional: true },
];

/**
 * Lookup data options shape used by OverviewStep.
 * Each consuming app provides its own lookup data through this interface.
 */
export interface ProductLookupOption {
  label: string;
  value: string;
}

/**
 * Lookup data provider interface that consuming apps must supply
 * to the AddProductDialog / OverviewStep components.
 */
export interface ProductLookupData {
  brandOptions: ProductLookupOption[];
  manufacturerOptions: ProductLookupOption[];
  formOptions: ProductLookupOption[];
  categoryOptions: ProductLookupOption[];
  isLoadingBrands?: boolean;
  /** Callback when manufacturer selection changes — app should fetch brands. */
  onManufacturerChange?: (manufacturer: string) => void;
  /** Create a new manufacturer locally (returns the name). */
  onCreateManufacturer?: (name: string) => Promise<string>;
  /** Create a new brand locally (returns the name). */
  onCreateBrand?: (name: string) => Promise<string>;
  /** Get measurement metric for a given form name. */
  getMeasurementMetricForForm?: (formName: string) => string | undefined;
  /**
   * Resolve hospital-specific Expiry After Mixing/Opening defaults (e.g. from
   * Settings > Expiry Rules) for the given product Form. Only consulted when
   * `requireExpiration` is set, and only fills fields that are currently empty.
   * Units reflect however the hospital configured the source rule — not
   * necessarily hours for mixing / days for opening.
   */
  getExpirationDefaults?: (formFactor: string) => Promise<{
    afterMixingHours?: string;
    afterMixingUnit?: 'hours' | 'days';
    afterOpeningDays?: string;
    afterOpeningUnit?: 'hours' | 'days';
  }>;
}

// -- Default form values ----------------------------------------
export function createDefaultOverview(): OverviewFormValues {
  return {
    basic_information: {
      sku: '', tms_product_id: '', brand_name: '', manufacturer: '',
      type_of_product: '', form: '', measurement_metric: '',
      calories_for_liquids: '', calories_for_pwds: '',
      flavor: '', product_status: '',
      hcpcs: '', ndc_format_code: '', age_for_use: '', region_for_use: '',
      date_verified: '',
    },
    description: {
      product_description: '', formula_classification: [],
      parenteral_category: '', substitutions: '',
    },
    allergens: {
      egg: false, fish: false, lactose: false, latex: false, milk: false,
      peanut: false, sesame: false, shellfish: false, soy: false, tree_nuts: false, wheat: false,
      contains_allergen_crustaceans: false, contains_allergen_molluscs: false,
      contains_allergen_celery: false, contains_allergen_mustard: false,
      contains_allergen_sulphur_dioxide: false, contains_allergen_sulphites: false,
      contains_allergen_lupin: false,
    },
    verification: { verification_source_url: '' },
    expiration: {
      expiration_after_mixing: '',
      expiration_after_mixing_unit: 'hours',
      expiration_after_opening: '',
      expiration_after_opening_unit: 'days',
    },
  };
}

export function createDefaultNutritions(): NutritionsFormValues {
  return {
    caloric_information: {
      displacement_for_pwd: '',
      osmolality: '', osmolarity: '', prsl: '', renal_solute_load: '',
      volume_to_provide_100_kcal: '', water: '',
    },
    protein: {
      protein: '', protein_source: '', protein_hydrolysis: '',
      bcaa: '', total_n2: '',
    },
    fat: {
      fat: '', mct: '', ala: '', linoleic_acid_omega_6: '',
      linolenic_acid_omega_3: '', ara: '', dha: '', epa: '', cholesterol: '',
    },
    carbohydrates: {
      cho: '', cho_source: '', added_sugars: '', total_sugars: '',
      dietary_fiber: '', soluble_fiber: '', insoluble_fiber: '',
      prebiotic_fiber: '', dextrose: '', d_galactose: '',
    },
    serving_information: {
      denominator_property: '', denominator_volume: '',
      recipe_dilution_for_reconstitution: '', one_level_unpacked_cup: '',
      one_level_unpacked_teaspoon: '', one_unpacked_level_scoop: '',
    },
    ingredients: { full_ingredients_list: '' },
    dietary_information: { gluten_free: '', halal: '', kosher: '' },
  };
}

export function createDefaultPackaging(): PackagingFormValues {
  return {
    primary_package: {
      two_d_barcode: '', unit_barcode: '', unit_form: '',
      unit_volume: '', volume_uom: '',
    },
    carton_case: {
      carton_barcode: '', carton_quantity_within_case: '',
      case_barcode: '', total_primary_units_in_carton: '',
      total_primary_units_in_case: '',
    },
    storage: {
      storage_opened: '', storage_unopened: '',
    },
  };
}

export function createDefaultVitamins(): VitaminsFormValues {
  return {
    b_vitamins: {
      biotin: '', folate: '', folic_acid: '', niacin: '',
      pantothenic_acid: '', riboflavin: '', thiamin: '',
      vitamin_b12: '', vitamin_b6: '',
    },
    fat_soluble_vitamins: {
      vitamin_a_iu: '', vitamin_a_rae: '', vitamin_d_iu: '',
      vitamin_d_mcg: '', vitamin_e_iu: '', vitamin_e_mg: '', vitamin_k: '',
    },
    other_vitamins_related: { choline: '', inositol: '', vitamin_c: '' },
  };
}

export function createDefaultMinerals(): MineralsFormValues {
  return {
    electrolytes: {
      acetate: '', chloride_meq: '', chloride_mg: '',
      potassium_meq: '', potassium_mg: '', sodium_meq: '', sodium_mg: '',
    },
    major_minerals: {
      calcium_meq: '', calcium_mg: '', magnesium_meq: '',
      magnesium_mg: '', phosphorus_mmol: '', phosphorus_mg: '',
    },
    trace_minerals: {
      aluminum: '', chromium: '', copper: '', fluoride: '',
      iodine: '', iron: '', manganese: '', molybdenum: '',
      selenium: '', zinc: '',
    },
  };
}

export function createDefaultAminoAcids(): AminoAcidsFormValues {
  return {
    amino_acid_derivatives: { carnitine: '', taurine: '' },
    essential_amino_acids: {
      histidine: '', isoleucine: '', leucine: '', lysine: '',
      methionine: '', phenylalanine: '', threonine: '',
      tryptophan: '', valine: '',
    },
    non_essential_amino_acids: {
      alanine: '', arginine: '', cystine: '', glycine: '',
      l_aspartic_acid: '', l_glutamic_acid: '', l_glutamine: '',
      proline: '', serine: '', tyrosine: '',
    },
  };
}

export function createDefaultOther(): OtherFormValues {
  return {
    identifiers: {
      over_arching_number_nutrition_source: '',
      over_arching_number_pens: '',
      over_arching_number_tmnp: '',
    },
    other_compounds: { lutein: '', mannitol: '', sodium_metabisulfite: '' },
    review_similar_products: {
      needs_review: '', similar_products: '', similarity_details: '',
    },
  };
}
